import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  formatSponsorText,
  hasSponsorLink,
  isExternalHref,
  shouldShowCampaignSponsorBlock,
} from '../../lib/sponsor';

interface CampaignSponsorBlockProps {
  sponsorNames: string[];
  sponsorLinkUrl?: string | null;
  align?: 'center' | 'left';
  className?: string;
}

const buttonClassName = cn(
  'inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-semibold',
  'bg-[var(--color-accent-subtle)] text-[var(--color-accent)] border border-[var(--color-accent)]/30',
  'shadow-[var(--shadow-sm)] hover:bg-[var(--color-accent)] hover:text-white',
  'transition-colors duration-[var(--duration-fast)]',
);

export function CampaignSponsorBlock({
  sponsorNames,
  sponsorLinkUrl,
  align = 'center',
  className,
}: CampaignSponsorBlockProps) {
  const sponsorText = formatSponsorText(sponsorNames);
  const showSponsorLine = Boolean(sponsorText);
  const showButton = hasSponsorLink(sponsorLinkUrl);

  if (!shouldShowCampaignSponsorBlock(sponsorNames, sponsorLinkUrl)) {
    return null;
  }

  const href = sponsorLinkUrl?.trim() ?? '';
  const external = showButton && isExternalHref(href);

  return (
    <div
      className={cn(
        'space-y-3',
        align === 'center' && 'flex flex-col items-center text-center',
        className,
      )}
    >
      {showSponsorLine && (
        <p className="text-sm text-[var(--color-text-secondary)]">
          Sponsored by{' '}
          <span className="font-medium text-[var(--color-text-primary)]">{sponsorText}</span>
        </p>
      )}
      {showButton &&
        (external ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className={buttonClassName}>
            Know your Sponsors
          </a>
        ) : (
          <Link to={href} className={buttonClassName}>
            Know your Sponsors
          </Link>
        ))}
    </div>
  );
}
